// src/pages/Dashboard.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useOutletContext } from "react-router-dom";

import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import { apiFetch } from "../api/http"; // ✅ token-aware helper

// --------------------------------------------
// Reusable card
// --------------------------------------------
const Card = ({ children, sx }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        borderRadius: 2,
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
};

// --------------------------------------------
// KPI Component
// --------------------------------------------
const Kpi = ({ label, icon, value, chipLabel, chipColor }) => {
  const theme = useTheme();

  const chipIsDefault = !chipColor || chipColor === "default";

  const chipMainColor = chipIsDefault
    ? theme.palette.text.secondary
    : theme.palette[chipColor]?.main || theme.palette.primary.main;

  return (
    <Card>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack spacing={0.5}>
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={600}
            textTransform="uppercase"
          >
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={800}>
            {value}
          </Typography>
        </Stack>

        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 99,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
          }}
        >
          {icon}
        </Box>
      </Stack>

      {chipLabel && (
        <Chip
          label={chipLabel}
          size="small"
          sx={{
            mt: 1.25,
            fontWeight: 600,
            bgcolor: alpha(chipMainColor, 0.15),
            color: chipMainColor,
          }}
        />
      )}
    </Card>
  );
};

// --------------------------------------------
// Stage name formatter
// --------------------------------------------
function stagePretty(stage) {
  if (!stage) return "Uncategorized";
  const s = stage.toLowerCase();
  if (s.includes("lead")) return "New Lead";
  if (s.includes("buyer")) return "Active Buyer";
  if (s.includes("listing") || s.includes("seller")) return "Active Listing";
  if (s.includes("contract")) return "Under Contract";
  if (s.includes("closed") || s.includes("won")) return "Closed";
  return stage;
}

// --------------------------------------------
// Page Component
// --------------------------------------------
export default function Dashboard() {
  const theme = useTheme();
  const { onAiStatusChange, aiRefreshToken } = useOutletContext() || {};

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [aiBusy, setAiBusy] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiError, setAiError] = useState("");

  // --------------------------------
  // Load clients (JWT-aware)
  // --------------------------------
  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        onAiStatusChange?.({ loading: true });

        const res = await apiFetch("/api/clients/");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        setClients(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error loading clients:", err);
        setLoadError("Unable to load clients from the server.");
        setClients([]);
      } finally {
        setLoading(false);
        onAiStatusChange?.({
          loading: false,
          lastUpdated: new Date().toISOString(),
        });
      }
    };

    loadClients();
  }, [aiRefreshToken, onAiStatusChange]);

  // --------------------------------
  // CRM metrics
  // --------------------------------
  const metrics = useMemo(() => {
    if (!clients.length) {
      return {
        totalClients: 0,
        buyers: 0,
        sellers: 0,
        newLeads: 0,
        activeDeals: 0,
        closedDeals: 0,
      };
    }

    const buyers = clients.filter((c) =>
      (c.client_type || "").toLowerCase().includes("buyer")
    ).length;

    const sellers = clients.filter((c) =>
      (c.client_type || "").toLowerCase().includes("seller")
    ).length;

    const newLeads = clients.filter((c) =>
      (c.stage || "").toLowerCase().includes("lead")
    ).length;

    const activeDeals = clients.filter((c) => {
      const s = (c.stage || "").toLowerCase();
      return s.includes("buyer") || s.includes("listing") || s.includes("contract");
    }).length;

    const closedDeals = clients.filter((c) =>
      (c.stage || "").toLowerCase().includes("closed")
    ).length;

    return {
      totalClients: clients.length,
      buyers,
      sellers,
      newLeads,
      activeDeals,
      closedDeals,
    };
  }, [clients]);

  // --------------------------------
  // Chart data
  // --------------------------------
  const pipelineChartData = useMemo(() => {
    if (!clients.length) return [];

    const groups = new Map();
    clients.forEach((c) => {
      const label = stagePretty(c.stage);
      groups.set(label, (groups.get(label) || 0) + 1);
    });

    return Array.from(groups.entries()).map(([name, count]) => ({
      name,
      count,
    }));
  }, [clients]);

  // --------------------------------
  // Lead lists
  // --------------------------------
  const newLeadList = useMemo(() => {
    const leads = clients.filter((c) =>
      (c.stage || "").toLowerCase().includes("lead")
    );
    if (!leads.length) return [];

    return [...leads].sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return db - da;
    });
  }, [clients]);

  const activeDealsList = useMemo(() => {
    const deals = clients.filter((c) => {
      const s = (c.stage || "").toLowerCase();
      return s.includes("buyer") || s.includes("listing") || s.includes("contract");
    });
    if (!deals.length) return [];

    return [...deals].sort((a, b) =>
      (a.stage || "").localeCompare(b.stage || "")
    );
  }, [clients]);

  // --------------------------------
  // AI follow-up planner
  // --------------------------------
  const callAi = async () => {
    if (!clients.length) {
      setAiText("Add a few clients first so AgentBlayna has data to work with.");
      setAiError("");
      return;
    }

    try {
      setAiBusy(true);
      setAiError("");
      setAiText("");
      onAiStatusChange?.({ loading: true });

      const snapshot = clients.slice(0, 60).map((c) => ({
        name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
        type: c.client_type || "",
        stage: c.stage || "",
        lead_source: c.lead_source || "",
        created_at: c.created_at || "",
      }));

      const messages = [
        {
          role: "system",
          content:
            "You are AgentBlayna, an AI assistant for a real estate CRM. " +
            "You are given a JSON array of clients with name, type, stage, lead_source, created_at. " +
            "Group clients into Hot, Warm, and Nurture with concrete actions.",
        },
        {
          role: "user",
          content: JSON.stringify(snapshot),
        },
      ];

      const res = await apiFetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "gpt-4o-mini", messages }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const reply =
        typeof data?.reply === "string"
          ? data.reply
          : data.reply?.content || "Unable to generate plan.";

      setAiText(reply);
    } catch (err) {
      console.error(err);
      setAiError("AgentBlayna could not reach the AI engine.");
    } finally {
      setAiBusy(false);
      onAiStatusChange?.({
        loading: false,
        lastUpdated: new Date().toISOString(),
      });
    }
  };

  const formatClientName = (c) =>
    `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Unnamed client";

  // --------------------------------
  // Render
  // --------------------------------
  return (
    <Stack spacing={3}>
      {/* Title */}
      <Stack direction="row" spacing={1} alignItems="center">
        <HomeWorkRoundedIcon sx={{ color: theme.palette.primary.main }} />
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            One clean view of your clients, deals, and follow-up.
          </Typography>
        </Box>
      </Stack>

      {/* Loading / error */}
      {loading && (
        <Typography variant="body2" color="text.secondary">
          Loading your clients…
        </Typography>
      )}
      {loadError && (
        <Typography variant="body2" color="error">
          {loadError}
        </Typography>
      )}

      {/* KPI row */}
      <Grid container spacing={2.25}>
        <Grid item xs={12} sm={6} md={3}>
          <Kpi
            label="Total clients"
            value={metrics.totalClients}
            icon={<PeopleAltRoundedIcon fontSize="small" />}
            chipLabel={`${metrics.buyers} buyers · ${metrics.sellers} sellers`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Kpi
            label="Active deals"
            value={metrics.activeDeals}
            icon={<BusinessCenterRoundedIcon fontSize="small" />}
            chipLabel={`${metrics.closedDeals} closed`}
            chipColor="primary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Kpi
            label="New leads"
            value={metrics.newLeads}
            icon={<PersonAddAltRoundedIcon fontSize="small" />}
            chipLabel={
              metrics.newLeads ? "Work these today" : "No new leads in the system"
            }
            chipColor={metrics.newLeads ? "warning" : "default"}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Kpi
            label="Buyers vs sellers"
            value={`${metrics.buyers}/${metrics.sellers}`}
            icon={<HomeWorkRoundedIcon fontSize="small" />}
            chipLabel="Pipeline mix"
            chipColor="secondary"
          />
        </Grid>
      </Grid>

      {/* Middle Section */}
      <Grid container spacing={2.25}>
        {/* Pipeline Chart */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: 340 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1.5}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <BoltRoundedIcon sx={{ color: theme.palette.primary.main }} />
                <Typography fontWeight={700}>Pipeline by stage</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Based on current client stages
              </Typography>
            </Stack>

            {!pipelineChartData.length ? (
              <Typography variant="body2" color="text.secondary">
                Once you add clients and stages, you’ll see your pipeline here.
              </Typography>
            ) : (
              <Box sx={{ height: 270 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pipelineChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: "#6B7280", fontSize: 12 }}
                    />
                    <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} />
                    <RTooltip
                      contentStyle={{
                        background: "#fff",
                        borderRadius: 10,
                        border: `1px solid ${theme.palette.divider}`,
                        fontSize: 12,
                      }}
                    />

                    <Bar
                      dataKey="count"
                      name="Clients"
                      radius={[6, 6, 0, 0]}
                      fill="url(#stageGradient)"
                    />

                    <defs>
                      <linearGradient id="stageGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#93C5FD" />
                        <stop offset="100%" stopColor="#2563EB" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Lead lists */}
        <Grid item xs={12} md={5}>
          <Stack spacing={2.25}>
            {/* New leads */}
            <Card>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography fontWeight={700}>New leads</Typography>
                <Chip
                  size="small"
                  label={`${newLeadList.length} in CRM`}
                  sx={{
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                  }}
                />
              </Stack>

              {!newLeadList.length ? (
                <Typography variant="body2" color="text.secondary">
                  No leads marked as “Lead” yet.
                </Typography>
              ) : (
                <List dense sx={{ mx: -1 }}>
                  {newLeadList.slice(0, 5).map((c) => (
                    <ListItem key={c.id} sx={{ px: 1 }}>
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography fontWeight={600}>
                              {formatClientName(c)}
                            </Typography>
                            <Chip
                              label={stagePretty(c.stage)}
                              size="small"
                              sx={{
                                borderRadius: 99,
                                fontSize: 11,
                                bgcolor: alpha(theme.palette.warning.main, 0.08),
                                color: theme.palette.warning.main,
                              }}
                            />
                          </Stack>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {c.lead_source
                              ? `Source: ${c.lead_source}`
                              : "Source not set"}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Card>

            {/* Active deals */}
            <Card>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography fontWeight={700}>Active deals</Typography>
                <Chip
                  size="small"
                  label={`${activeDealsList.length} in pipeline`}
                  sx={{
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                  }}
                />
              </Stack>

              {!activeDealsList.length ? (
                <Typography variant="body2" color="text.secondary">
                  Mark clients as Buyer/Listing/Under Contract to track deals.
                </Typography>
              ) : (
                <List dense sx={{ mx: -1 }}>
                  {activeDealsList.slice(0, 5).map((c) => (
                    <ListItem key={c.id} sx={{ px: 1 }}>
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography fontWeight={600}>
                              {formatClientName(c)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {stagePretty(c.stage)}
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {c.client_type || "Client type not set"}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Bottom section — AgentBlayna AI */}
      <Grid container spacing={2.25}>
        <Grid item xs={12} md={8}>
          <Card>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1.5}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <ChatRoundedIcon sx={{ color: theme.palette.primary.main }} />
                <Typography fontWeight={700}>AgentBlayna</Typography>
              </Stack>

              <Chip
                size="small"
                icon={<StarBorderRoundedIcon sx={{ fontSize: 14 }} />}
                label="AI follow-up planner"
                sx={{
                  height: 24,
                  borderRadius: 99,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  "& .MuiChip-label": { fontSize: 11, fontWeight: 600 },
                }}
              />
            </Stack>

            <Typography variant="body2" color="text.secondary" mb={1.5}>
              Click below and AgentBlayna will analyze your clients and give you
              a 7-day follow-up plan ranked by urgency.
            </Typography>

            <Button
              variant="contained"
              startIcon={<BoltRoundedIcon />}
              onClick={callAi}
              disabled={aiBusy}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 700,
                mb: 1.5,
              }}
            >
              {aiBusy ? "AgentBlayna is thinking…" : "Generate follow-up plan"}
            </Button>

            {aiError && (
              <Typography variant="body2" color="error" mb={1}>
                {aiError}
              </Typography>
            )}

            <Box
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
                p: 1.5,
                minHeight: 140,
              }}
            >
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {aiBusy
                  ? "Reviewing your clients and stages…"
                  : aiText ||
                    "Your AI plan will appear here. Great place to copy-paste into your daily call sheet."}
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Quick wins */}
        <Grid item xs={12} md={4}>
          <Card>
            <Typography fontWeight={700} mb={1}>
              Today’s quick wins
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1.25}>
              A few ideas to get immediate value from AgentBlayna:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="Clean up your stages"
                  secondary="Update clients to Lead, Buyer, Listing, or Closed so your pipeline reflects reality."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Tag lead sources"
                  secondary="Set Zillow, referral, or open house so you can track ROI."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Run your first AI plan"
                  secondary="Use AgentBlayna's plan as your call list for today."
                />
              </ListItem>
            </List>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
